import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { orgMonth } from '@/lib/today';
import * as React from 'react';

import {
  Alert, AlertDescription, Button, Card, CardContent, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { ExportMenu } from '@/components/export-menu';
import { downloadCsv } from '@/lib/csv';
import { downloadXlsx } from '@/lib/xlsx';
import { usePeriodShortcuts } from '@/lib/shortcuts';
import {
  ABSENCE_TYPES, TYPE_COLOR, TYPE_LABEL, useAbsenceOverview,
} from '@/lib/absence-overview-api';

/**
 * Leave taken this month, per person, per type.
 *
 * Counts APPROVED leave only - a pending request is not a day anybody has taken,
 * and a report that includes them overstates what came off entitlement. The
 * calendar shows pending because planning needs it; this counts, so it does not.
 *
 * The figures use the same rule that deducted the days from each balance:
 * weekends, contracted days off and mandatory public holidays are not leave. So a
 * Friday-to-Monday absence is two days here, matching the two taken off the
 * balance rather than the four calendar days it spans.
 */
export function AbsenceReportPage() {
  const [month, setMonth] = React.useState(() => orgMonth());

  // Left/right or j/k to move a period, t for today. See lib/shortcuts.
  usePeriodShortcuts({
    previous: () => setMonth(shiftMonth(month, -1)),
    next: () => setMonth(shiftMonth(month, 1)),
    today: () => setMonth(orgMonth()),
  });
  const { data, isPending, isError, error } = useAbsenceOverview(month);

  const rows = data?.rows.filter((row) => row.totalDays > 0) ?? [];
  const totals = React.useMemo(() => {
    const byType: Record<string, number> = {};
    let total = 0;
    for (const row of rows) {
      for (const type of ABSENCE_TYPES) {
        byType[type] = (byType[type] ?? 0) + (row.byType[type] ?? 0);
      }
      total += row.totalDays;
    }
    return { byType, total };
  }, [rows]);

  // Built once and written either way, so the two files cannot disagree.
  const columns = [
    { header: 'Person', width: 26 },
    ...ABSENCE_TYPES.map((t) => ({ header: TYPE_LABEL[t]!, width: 14, numeric: true })),
    { header: 'Total', width: 12, numeric: true },
  ];

  const exportRows = () => [
    ...rows.map((row) => [row.name, ...ABSENCE_TYPES.map((t) => row.byType[t] ?? 0), row.totalDays]),
    ['Total', ...ABSENCE_TYPES.map((t) => totals.byType[t] ?? 0), totals.total],
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Absence report"
        description="Approved leave this month, by person and type."
        actions={
          rows.length > 0 ? (
            <ExportMenu
              label="Export"
              onCsv={() =>
                downloadCsv(`absence-${month}.csv`, [columns.map((c) => c.header), ...exportRows()])
              }
              onXlsx={() => downloadXlsx(`absence-${month}.xlsx`, 'Absence', columns, exportRows())}
            />
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" aria-label="Previous month" title="Previous month (← or j)"
          onClick={() => setMonth(shiftMonth(month, -1))}>
          <ChevronLeft className="size-4" />
        </Button>
        <span className="ts-num min-w-[9rem] text-center text-sm font-semibold">
          {monthLabel(month)}
        </span>
        <Button variant="outline" size="icon" aria-label="Next month" title="Next month (→ or k)"
          onClick={() => setMonth(shiftMonth(month, 1))}>
          <ChevronRight className="size-4" />
        </Button>
        <Button hint={"Jump to the current month"} variant="outline" onClick={() => setMonth(orgMonth())}>
          This month
        </Button>
      </div>

      {isError && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">
            {error instanceof Error ? error.message : 'Could not load that month.'}
          </AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-72 w-full" />}

      {data && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table className="min-w-[640px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    {ABSENCE_TYPES.map((type) => (
                      <TableHead key={type} className="text-right">
                        <span className="flex items-center justify-end gap-1.5">
                          <span
                            className="size-2.5 rounded-[2px]"
                            style={{ backgroundColor: TYPE_COLOR[type] }}
                          />
                          {TYPE_LABEL[type]}
                        </span>
                      </TableHead>
                    ))}
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.employeeId}>
                      <TableCell className="whitespace-nowrap font-medium">{row.name}</TableCell>
                      {ABSENCE_TYPES.map((type) => (
                        <TableCell key={type} className="ts-num text-right">
                          {/* A zero is drawn as a dash: a column of noughts reads as
                              data, and the eye has to work out which are real. */}
                          {row.byType[type] ? (
                            row.byType[type]
                          ) : (
                            <span className="opacity-30">—</span>
                          )}
                        </TableCell>
                      ))}
                      <TableCell className="ts-num text-right font-semibold">
                        {row.totalDays}
                      </TableCell>
                    </TableRow>
                  ))}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={ABSENCE_TYPES.length + 2}
                        className="py-10 text-center text-muted-foreground"
                      >
                        No approved leave this month.
                      </TableCell>
                    </TableRow>
                  )}

                  {rows.length > 0 && (
                    <TableRow className="bg-muted/40">
                      <TableCell className="font-semibold">
                        Total
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          {rows.length} {rows.length === 1 ? 'person' : 'people'}
                        </span>
                      </TableCell>
                      {ABSENCE_TYPES.map((type) => (
                        <TableCell key={type} className="ts-num text-right font-semibold">
                          {totals.byType[type] || <span className="opacity-30">—</span>}
                        </TableCell>
                      ))}
                      <TableCell className="ts-num text-right font-semibold">
                        {totals.total}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}


const UTC = { timeZone: 'UTC' } as const;

function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00.000Z`).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    ...UTC,
  });
}

function shiftMonth(month: string, delta: number): string {
  const [year, m] = month.split('-').map(Number);
  return new Date(Date.UTC(year!, m! - 1 + delta, 1)).toISOString().slice(0, 7);
}
